import { useTranslation } from 'react-i18next';
import { Price } from '@hero/web-components';
import { FileTextIcon, LockIcon } from '@hero/web-icons';
import type { FinancingLenderView } from '@hero/api';

export type LenderViewDetailProps = {
  view: FinancingLenderView;
};

/**
 * Full underwriting packet for the lender-view page.
 *
 * Layout (desktop, 3-col grid; mobile stacks):
 *  1. **Invoice + payer card** (col-span-2) — headline requested
 *     amount, then a 2-column definition list combining the invoice
 *     metadata with the invoice-payer (client) info and the signed
 *     agreement reference. External invoices surface the payer
 *     block; Hero invoices fold into a single-column metadata list.
 *     We merge these into one card so the lender sees the whole
 *     "what's being financed" story in one scan instead of having
 *     to bounce between an invoice card and a payer card at the
 *     bottom of the page.
 *  2. **Contact card** — **masked** until `view.contactRevealed`
 *     is true. The mask is enforced server-side; this component
 *     just renders whatever the backend chose to send. We still
 *     show the section with a structured "hidden" affordance so
 *     the lender knows the field exists and what they'll get.
 *  3. **Self-declared business profile** (col-span-3) — legal
 *     entity (CR + tax + legal name + established year), fleet /
 *     revenue tiers, financing-need flags. CR and tax document
 *     links open in a new tab so the lender can verify each
 *     against the registration before funding.
 *  4. **Financial statements** (col-span-3) — explicit empty state
 *     when no docs were uploaded so the absence is itself a signal.
 */
export function LenderViewDetail({ view }: LenderViewDetailProps) {
  /**
   * Load three namespaces so the row values resolve against the
   * right shared dictionaries:
   *   - `m-financing` for all the `partner_page.*` labels + the
   *     enum-driven keys it owns (`invoice_source.*`, `fleet_tier.*`,
   *     `revenue_tier.*`, `financing_need.*`).
   *   - `c-main` for `business_type.*` (organisation legal form).
   *   - `m-agreement` for the `status.*` enum on the bilateral
   *     agreement carried alongside external invoices.
   * Using prefixed `t('ns:key')` is more explicit than relying on
   * fallback namespace order, and makes greps easier.
   */
  const { t } = useTranslation(['m-financing', 'c-main', 'm-agreement']);
  const { application, org, client, agreement, contactRevealed } = view;
  const { invoice, requested, profileSnapshot } = application;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardTitle>{t('partner_page.sections.invoice')}</CardTitle>
        {/**
         * Requested amount is the lender's headline number — surface
         * it above the standard definition list with prominent size
         * + brand colour so it doesn't get lost among the other
         * invoice metadata.
         */}
        <div className="mb-4 rounded-xl border border-primary/20 bg-primary/5 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">
            {t('partner_page.fields.requested_amount')}
          </p>
          <div className="mt-1 flex items-baseline gap-2">
            <Price
              amount={requested.amount}
              currency={invoice.currency}
              size="xl"
              className="font-bold text-primary"
            />
            <span className="text-sm text-gray-600">
              {t('partner_page.requested.over')} {requested.term.value}{' '}
              {requested.term.type}
            </span>
          </div>
        </div>
        {/**
         * Two-column definition grid combining invoice metadata
         * (left) with the invoice-payer / agreement block (right).
         * Hero invoices have no client/agreement payload so the
         * grid degrades to invoice-only rows; external invoices
         * fan the payer rows into the right column.
         */}
        <DefinitionList columns={2}>
          <Row label={t('partner_page.fields.invoice_amount')}>
            <Price amount={invoice.amount} currency={invoice.currency} />
          </Row>
          <Row label={t('partner_page.fields.expected_due_date')}>
            {formatDate(invoice.expectedDueDate)}
          </Row>
          <Row label={t('partner_page.fields.invoice_source')}>
            {t(`invoice_source.${invoice.source}`)}
          </Row>
          {invoice.invoiceNumber && (
            <Row label={t('partner_page.fields.invoice_number')}>
              <span className="font-mono">{invoice.invoiceNumber}</span>
            </Row>
          )}
          <Row label={t('partner_page.fields.client_name')}>
            {client?.name ?? invoice.clientName}
          </Row>
          {invoice.documentUrl && (
            <Row label={t('partner_page.fields.document')}>
              <a
                href={invoice.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-600 hover:underline">
                {t('partner_page.fields.view_document')}
              </a>
            </Row>
          )}
          {agreement && (
            <Row label={t('partner_page.fields.agreement_status')}>
              {t(`m-agreement:status.${agreement.status}` as never)}
            </Row>
          )}
          {agreement?.contractPDF?.url && (
            <Row label={t('partner_page.fields.contract_pdf')}>
              <a
                href={agreement.contractPDF.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-600 hover:underline">
                {t('partner_page.fields.view_document')}
              </a>
            </Row>
          )}
          {client?.contact?.name && (
            <Row label={t('partner_page.fields.client_contact_name')}>
              {client.contact.name}
            </Row>
          )}
          {client?.contact?.phone && (
            <Row label={t('partner_page.fields.client_contact_phone')}>
              <a
                href={`tel:${client.contact.phone}`}
                dir="ltr"
                className="font-mono text-blue-600 hover:underline">
                {client.contact.phone}
              </a>
            </Row>
          )}
        </DefinitionList>
      </Card>

      <Card>
        <CardTitle>{t('partner_page.sections.contact')}</CardTitle>
        {contactRevealed ? (
          <DefinitionList>
            <Row label={t('partner_page.fields.carrier_name')}>{org.name}</Row>
            {org.contact?.name && (
              <Row label={t('partner_page.fields.contact_name')}>
                {org.contact.name}
              </Row>
            )}
            {org.contact?.phone && (
              <Row label={t('partner_page.fields.contact_phone')}>
                <a
                  href={`tel:${org.contact.phone}`}
                  dir="ltr"
                  className="font-mono text-blue-600 hover:underline">
                  {org.contact.phone}
                </a>
              </Row>
            )}
            {org.contact?.email && (
              <Row label={t('partner_page.fields.contact_email')}>
                <a
                  href={`mailto:${org.contact.email}`}
                  className="text-blue-600 hover:underline">
                  {org.contact.email}
                </a>
              </Row>
            )}
            <Row label={t('partner_page.fields.months_active')}>
              {org.monthsActive}
            </Row>
          </DefinitionList>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
            <div className="flex items-center gap-2 text-gray-700">
              <LockIcon className="size-4" />
              <span className="font-medium">
                {t('partner_page.masked.title')}
              </span>
            </div>
            <p className="mt-1.5 leading-relaxed">
              {t('partner_page.masked.description')}
            </p>
          </div>
        )}
      </Card>

      <Card className="lg:col-span-3">
        <CardTitle>{t('partner_page.sections.profile')}</CardTitle>
        {/**
         * Sub-section: corporate registration. Renders ONLY when
         * the carrier has at least one legal field on file —
         * otherwise we collapse it so the profile section doesn't
         * lead with a giant empty block. CR + Tax documents open
         * in a new tab so the lender can verify each scan
         * separately without losing the underwriting packet.
         */}
        {(org.legalName ||
          org.crNumber ||
          org.crImageUrl ||
          org.taxNumber ||
          org.taxImageUrl ||
          org.businessType ||
          org.establishedYear) && (
          <div className="mb-5">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('partner_page.sections.legal_entity')}
            </h3>
            <DefinitionList columns={2}>
              {org.legalName && (
                <Row label={t('partner_page.fields.legal_name')}>
                  {org.legalName}
                </Row>
              )}
              {org.businessType && (
                <Row label={t('partner_page.fields.business_type')}>
                  {t(`c-main:business_type.${org.businessType}` as never)}
                </Row>
              )}
              {org.crNumber && (
                <Row label={t('partner_page.fields.cr_number')}>
                  <span className="font-mono">{org.crNumber}</span>
                </Row>
              )}
              {org.crImageUrl && (
                <Row label={t('partner_page.fields.cr_image')}>
                  <a
                    href={org.crImageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline">
                    <FileTextIcon className="size-4" />
                    {t('partner_page.fields.view_document')}
                  </a>
                </Row>
              )}
              {org.taxNumber && (
                <Row label={t('partner_page.fields.tax_number')}>
                  <span className="font-mono">{org.taxNumber}</span>
                </Row>
              )}
              {org.taxImageUrl && (
                <Row label={t('partner_page.fields.tax_image')}>
                  <a
                    href={org.taxImageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline">
                    <FileTextIcon className="size-4" />
                    {t('partner_page.fields.view_document')}
                  </a>
                </Row>
              )}
              {org.establishedYear && (
                <Row label={t('partner_page.fields.established_year')}>
                  {org.establishedYear}
                </Row>
              )}
            </DefinitionList>
          </div>
        )}

        {/**
         * Sub-section: business/financing tiers. Always rendered
         * (the profile snapshot always carries the tier fields once
         * a carrier has saved their financing profile).
         */}
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t('partner_page.sections.business_profile')}
        </h3>
        <DefinitionList columns={2}>
          {profileSnapshot.fleetTier && (
            <Row label={t('partner_page.fields.fleet_tier')}>
              {t(`fleet_tier.${profileSnapshot.fleetTier}`)}
            </Row>
          )}
          {profileSnapshot.annualRevenueTier && (
            <Row label={t('partner_page.fields.annual_revenue_tier')}>
              {t(`revenue_tier.${profileSnapshot.annualRevenueTier}`)}
            </Row>
          )}
          <Row label={t('partner_page.fields.owns_all_trucks')}>
            {yesNo(t, profileSnapshot.ownsAllTrucks)}
          </Row>
          {profileSnapshot.trucksOwnedCount !== undefined && (
            <Row label={t('partner_page.fields.trucks_owned_count')}>
              {profileSnapshot.trucksOwnedCount}
            </Row>
          )}
          <Row label={t('partner_page.fields.has_loans')}>
            {yesNo(t, profileSnapshot.hasLoans)}
          </Row>
          {profileSnapshot.monthlyFinancingPayment !== undefined && (
            <Row label={t('partner_page.fields.monthly_financing_payment')}>
              <Price
                amount={profileSnapshot.monthlyFinancingPayment}
                currency="SAR"
              />
            </Row>
          )}
          {/**
           * `isProfitable` is `optional` on the profile — carriers
           * may legitimately skip the question. Render the row only
           * when answered so unfilled fields don't pollute the
           * underwriting packet with a meaningless "—".
           */}
          {profileSnapshot.isProfitable !== undefined && (
            <Row label={t('partner_page.fields.is_profitable')}>
              {yesNo(t, profileSnapshot.isProfitable)}
            </Row>
          )}
          {profileSnapshot.financingNeeds &&
            profileSnapshot.financingNeeds.length > 0 && (
              <Row label={t('partner_page.fields.financing_needs')}>
                {profileSnapshot.financingNeeds
                  .map(p => t(`financing_need.${p}`))
                  .join(', ')}
              </Row>
            )}
        </DefinitionList>
      </Card>

      {/**
       * Financial statements — P&L, balance sheet, etc. Always
       * surface the section so the lender can see at-a-glance
       * whether the carrier provided any supporting docs (an
       * empty list is itself a signal). When non-empty we list
       * each upload with a direct link to the asset; when empty
       * we render an explicit empty state instead of hiding the
       * section.
       */}
      <Card className="lg:col-span-3">
        <CardTitle>{t('partner_page.sections.financial_statements')}</CardTitle>
        {profileSnapshot.financialStatements &&
        profileSnapshot.financialStatements.length > 0 ? (
          <ul className="space-y-2">
            {profileSnapshot.financialStatements.map((statement, index) => (
              <li key={statement.id}>
                <a
                  href={statement.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline">
                  <FileTextIcon className="size-4" />
                  {t('partner_page.fields.financial_statement_n', {
                    n: index + 1,
                  })}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
            <div className="flex items-center gap-2 text-gray-700">
              <FileTextIcon className="size-4" />
              <span className="font-medium">
                {t('partner_page.financial_statements.empty_title')}
              </span>
            </div>
            <p className="mt-1.5 leading-relaxed">
              {t('partner_page.financial_statements.empty_description')}
            </p>
          </div>
        )}
      </Card>

      {/*
       * Note: the previous standalone "Invoice payer" + "Agreement"
       * cards were folded into the invoice card above (right column
       * of the 2-col grid below the requested-amount banner). This
       * keeps the "what's being financed" story in a single visual
       * unit instead of forcing the lender to scroll to the bottom
       * to find the payer info. The backend still gates `client` /
       * `agreement` server-side (external invoices only); when both
       * are undefined the merged grid simply degrades to invoice
       * metadata alone.
       */}
    </div>
  );
}

function yesNo(
  t: ReturnType<typeof useTranslation<'m-financing'>>['t'],
  value: unknown,
): string {
  if (value === true || value === 'yes') return t('lender_page.row_details.yes');
  if (value === false || value === 'no') return t('lender_page.row_details.no');
  return '—';
}

function formatDate(value: Date | string | number): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toISOString().slice(0, 10);
}

function Card({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6 ${className ?? ''}`}>
      {children}
    </section>
  );
}

function CardTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {children}
    </h2>
  );
}

function DefinitionList({
  children,
  columns = 1,
}: {
  children: React.ReactNode;
  columns?: 1 | 2;
}) {
  return (
    <dl
      className={`grid gap-x-6 gap-y-3 ${columns === 2 ? 'sm:grid-cols-2' : ''}`}>
      {children}
    </dl>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="text-sm text-gray-900">{children}</dd>
    </div>
  );
}
